import { useEffect, useRef, useMemo } from 'react'
import '../views/PatrimoineSection.css'
import '../style.css'

const pointsInteret = [
  {
    id: 1,
    titre: 'Château fort d’Écaussinnes-Lalaing',
    sousTitre: 'Château médiéval',
    description:
      'Un château emblématique datant du XIIᵉ siècle, avec ses tours et remparts. Idéal pour une photo ou une petite visite historique.',
    images: ['/img/Chateau Ecaussinnes1.jpeg', '/img/Chateau Ecaussinnes2.webp'],
    mapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Ch%C3%A2teau+fort+d%E2%80%99%C3%89caussinnes-Lalaing',
    distanceKm: 0.5, // exemple à ajuster si tu as des valeurs précises
  },
  {
    id: 2,
    titre: 'Château de la Follie',
    sousTitre: 'Architecture et parc',
    description:
      'Un château entouré de verdure, idéal pour une balade tranquille aux abords du village.',
    images: ['/img/Folie2.jpeg', '/img/Folie2.jpg'],
    mapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Ch%C3%A2teau+de+la+Follie+%C3%89caussinnes',
    distanceKm: 1.2,
  },
  {
    id: 3,
    titre: 'Bois de la Houssière',
    sousTitre: 'Balade nature',
    description:
      'Une grande étendue boisée avec sentiers balisés, parfaite pour une promenade digestive après un cornet de frites.',
    images: ['/img/Houssiere1.jpg', '/img/Houssiere2.jpg'],
    mapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Bois+de+la+Houssi%C3%A8re',
    distanceKm: 3.5,
  },
  {
    id: 4,
    titre: 'Plan incliné de Ronquières',
    sousTitre: 'Ouvrage d’art',
    description:
      'Un impressionnant ouvrage d’ingénierie permettant aux bateaux de franchir un important dénivelé. Vue panoramique sur le canal.',
    images: ['/img/Ronquières1.webp', '/img/Ronquières2.jpg'],
    mapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Plan+inclin%C3%A9+de+Ronqui%C3%A8res',
    distanceKm: 9.0,
  },
  {
    id: 5,
    titre: 'Promenade le long du canal',
    sousTitre: 'Balade au fil de l’eau',
    description:
      'Une promenade paisible le long du canal, entre écluses et paysages ouverts, accessible en quelques minutes en voiture.',
    images: ['/img/Canal1.jpg', '/img/Canal2.png'],
    mapsUrl:
      'https://www.google.com/maps/search/?api=1&query=R%C3%A9serve+naturelle+de+Ronqui%C3%A8res',
    distanceKm: 8.5,
  },
  {
    id: 6,
    titre: 'Parc de la Marlière',
    sousTitre: 'Pause détente',
    description:
      'Petit parc charmant dans le centre, idéal pour se détendre, faire un pique-nique ou digérer vos frites après la visite de la friterie.',
    images: ['/img/Marlière1.jpg', '/img/Marlière2.png'],
    mapsUrl:
      'https://www.google.com/maps/search/?api=1&query=Parc+de+la+Marli%C3%A8re+%C3%89caussinnes',
    distanceKm: 0.8,
  },
]

const PatrimoineSection = () => {
  const listRef = useRef(null)

  // On trie une fois les points par distance croissante
  const sortedPoints = useMemo(
    () => [...pointsInteret].sort((a, b) => a.distanceKm - b.distanceKm),
    []
  )

  // Animation d’apparition au scroll
  useEffect(() => {
    const root = listRef.current
    if (!root) return

    const elements = root.querySelectorAll('.patrimoine-section-item')
    if (!elements.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.2 }
    )

    elements.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <section className="patrimoines-container">
      {/* HERO PATRIMOINE */}
      <section className="patrimoine-hero">
        <img
          src="/img/Chateau Ecaussinnes1.jpeg"
          alt="Vue du château d’Écaussinnes-Lalaing et du patrimoine environnant"
          className="patrimoine-hero-image"
        />
        <div className="patrimoine-hero-overlay" />
        <div className="patrimoine-hero-content">
          <h1 className="patrimoine-hero-title">Patrimoine local</h1>
          <p className="patrimoine-hero-subtitle">
            À quelques minutes de la friterie, des lieux chargés d&apos;histoire
            et de nature à découvrir en balade.
          </p>
        </div>
        <div className="scroll-indicator" aria-hidden="true">
          <span className="scroll-indicator-chevron" />
        </div>
      </section>

      {/* CONTENU PRINCIPAL */}
      <main className="patrimoine-main">
        <header className="patrimoine-header">
          <h2 className="patrimoine-section-title">
            À voir autour de la friterie
          </h2>
          <p className="patrimoine-section-subtitle">
            Découvrez ces six points d’intérêt proches d’Écaussinnes-Lalaing,
            classés par ordre de proximité par rapport à la friterie.
            Cliquez sur un lieu pour l’ouvrir dans Google&nbsp;Maps dans un nouvel onglet.
          </p>
        </header>

        {/* SECTIONS EN ENFILADE (scroll progressif) */}
        <section
          ref={listRef}
          className="patrimoine-list"
          aria-label="Lieux d’intérêt à proximité"
        >
          {sortedPoints.map((point, index) => {
            const isEven = index % 2 === 0

            return (
              <article
                key={point.id}
                className={
                  'patrimoine-section-item ' +
                  (isEven
                    ? 'patrimoine-section-item--image-left'
                    : 'patrimoine-section-item--image-right')
                }
              >
                <a
                  href={point.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="patrimoine-section-link"
                  aria-label={`${point.titre} – ouvrir dans Google Maps`}
                >
                  <div className="patrimoine-section-image-wrapper">
                    <div className="patrimoine-section-image-grid">
                      {point.images.map((src, idx) => (
                        <div
                          key={idx}
                          className="patrimoine-section-image-cell"
                        >
                          <img
                            src={src}
                            alt={point.titre}
                            className="patrimoine-section-image"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="patrimoine-section-image-overlay" />
                  </div>

                  <div className="patrimoine-section-text">
                    <h3 className="patrimoine-card-title">{point.titre}</h3>
                    <p className="patrimoine-card-subtitle">
                      {point.sousTitre}
                      {typeof point.distanceKm === 'number' && (
                        <>
                          {' · '}
                          <span className="patrimoine-card-distance">
                            {point.distanceKm.toLocaleString('fr-BE', {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })}{' '}
                            km
                          </span>
                        </>
                      )}
                    </p>
                    <p className="patrimoine-card-text">
                      {point.description}
                    </p>
                    <span className="patrimoine-card-cta">
                      Ouvrir dans Google Maps
                    </span>
                  </div>
                </a>
              </article>
            )
          })}
        </section>
      </main>
    </section>
  )
}

export default PatrimoineSection